import { T } from '@transifex/react';
import { cn, Divider, HStack, Skeleton, Text, VStack } from '@vidahealth/vida-ui';
import { Pressable, View } from 'react-native';

import { coachName } from '@/components/CoachSummary';
import type { MatchedCoach } from '@/modules/api';

import { NATIVE_CARD_SHADOW } from '../constants/cardShadow';
import { useNextAppointmentLabel } from '../hooks/useNextAppointmentLabel';

import { CoachAvatar } from './CoachAvatar';

/**
 * Private to CoachMatchingScreen — a compact coach row (avatar + name + title +
 * next appointment) for the "More health experts" list below the recommended
 * card.
 */
export function CompactCoachCard({
  coach,
  selected,
  onPress,
  onReadBio,
}: {
  coach: MatchedCoach;
  selected: boolean;
  onPress: () => void;
  onReadBio: () => void;
}) {
  const { label: nextAppointment, isLoading: loadingAppointment } = useNextAppointmentLabel(
    coach.userUuid
  );

  return (
    <View style={NATIVE_CARD_SHADOW} className='rounded-lg shadow-hard-5'>
      <View
        className={cn(
          'overflow-hidden rounded-lg border border-outline-50',
          selected ? 'border-2 border-primary-500' : ''
        )}
      >
        <Pressable
          accessibilityRole='button'
          accessibilityState={{ selected }}
          onPress={onPress}
          testID={`compact-${coach.uuid}`}
          className='bg-background-0 p-6 dark:bg-background-50'
        >
          <VStack space='lg'>
            <HStack space='md' className='items-center'>
              <CoachAvatar coach={coach} size='lg' />
              <VStack space='xs' className='flex-1'>
                <Text size='md' weight='bold' className='text-typography-900'>
                  {coachName(coach)}
                </Text>
                {coach.title ? (
                  <Text size='sm' className='font-body text-typography-900'>
                    {coach.title}
                  </Text>
                ) : null}
                {coach.bio ? (
                  <Pressable
                    onPress={onReadBio}
                    testID={`compact-${coach.uuid}-read-bio`}
                    className='self-start'
                  >
                    <Text size='sm' className='font-body-semibold text-primary-500'>
                      <T _str='Read full bio' />
                    </Text>
                  </Pressable>
                ) : null}
              </VStack>
            </HStack>
            {loadingAppointment || nextAppointment ? (
              <>
                <Divider className='bg-outline-100' />
                <VStack space='xs'>
                  <Text size='sm' className='font-body-semibold text-typography-900'>
                    <T _str='Next appointment' />
                  </Text>
                  {loadingAppointment ? (
                    <Skeleton variant='rounded' className='h-4 w-3/4' />
                  ) : (
                    <Text size='sm' className='font-body text-typography-600' numberOfLines={1}>
                      {nextAppointment}
                    </Text>
                  )}
                </VStack>
              </>
            ) : null}
          </VStack>
        </Pressable>
      </View>
    </View>
  );
}
